// Hotbar, pack panel (I / Tab) with armor slot, and crafting. Plain DOM over the canvas.
// Moving things: click a slot to pick it up, click another to drop/swap it there (Esc cancels).
import { HOTBAR, ITEMS, PACK_SIZE, RECIPES, count, type ItemDef, type ItemId, type Pack, type Slot } from '../../shared/items.ts';
import type { Net } from './net.ts';
import { DRIVES, MACHINES, type MachineKind } from '../../shared/machines.ts';
import { SIGILS } from '../../shared/blood.ts';
import { AFFIXES, RARITY, cantSet } from '../../shared/loot.ts';
import type { ServerMsg } from '../../shared/protocol.ts';
type MachineMsg = Extract<ServerMsg, { t: 'machine' }>;
type TermMsg = Extract<ServerMsg, { t: 'term' }>;
import type { Sfx } from './sfx.ts';

type IconMeta = Record<string, [number, number, number, number]>;

const $ = (id: string) => document.getElementById(id)!;

export function iconStyle(meta: IconMeta, id: string): string {
  const box = meta[id];
  if (!box) return `background:${ITEMS[id as ItemId]?.color ?? '#555'}`;
  return `background-image:url(/art/icons.png);background-position:-${box[0]}px -${box[1]}px`;
}

function describe(d: ItemDef): string {
  const bits: string[] = [];
  if (d.axe) bits.push(`axe, tier ${d.axe}`);
  if (d.pick) bits.push(`pickaxe, tier ${d.pick}`);
  if (d.damage) bits.push(`${d.damage} damage`);
  if (d.armor) bits.push(`${d.armor} armor`);
  if (d.heal) bits.push(`heals ${d.heal} (right-click to eat)`);
  if (d.places) bits.push('right-click to place');
  if (d.sigil) bits.push(`sigil: ${SIGILS[d.sigil].text} Costs ${SIGILS[d.sigil].lp} LP`);
  if (d.dagger) bits.push('right-click a blood altar to bleed into it');
  return bits.length ? `${d.name} — ${bits.join(', ')}` : d.name;
}

/** Tooltip for a whole slot: a magic item's name, rarity and affixes, then the base item. */
function describeSlot(s: NonNullable<Slot>): string {
  if (!s.m) return describe(ITEMS[s.id]);
  const lines = s.m.a.map(([k, v]) => `  • ${AFFIXES[k].text(v)}`);
  return `${s.m.name} (${RARITY[s.m.r].name})&#10;${describe(ITEMS[s.id])}&#10;${lines.join('&#10;')}`;
}

const rarityClass = (s: Slot) => (s?.m ? (s.m.r === 3 ? ' unique' : s.m.r === 2 ? ' rare' : ' magic') : '');

export class PackUi {
  pack: Pack | null = null;
  private holding: number | null = null; // slot index being moved, -1 = armor
  open = false;
  private chest: Slot[] | null = null;
  private machine: MachineMsg | null = null;
  private term: TermMsg | null = null;
  private bench = false;
  /** Set by the scene: is that station close enough to use? */
  nearStation: (st: 'cooking_fire' | 'furnace') => boolean = () => false;
  private stationWas = 0;

  constructor(private net: Net, private sfx: Sfx, private icons: IconMeta) {
    const search = $('craft-search') as HTMLInputElement;
    search.addEventListener('input', () => this.render());
    search.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        if (search.value) search.value = '';
        else search.blur();
        this.render();
      }
    });
    const tsearch = $('term-search') as HTMLInputElement;
    tsearch.addEventListener('input', () => this.render());
    tsearch.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        if (tsearch.value) tsearch.value = '';
        else tsearch.blur();
        this.render();
      }
    });
    $('term-store-all').addEventListener('click', () => this.net.send({ t: 'termStore', i: -1 }));
    $('hotbar').addEventListener('mousedown', (e) => e.stopPropagation());
    $('pack-panel').addEventListener('mousedown', (e) => e.stopPropagation());
    document.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'i' || e.key === 'I' || e.key === 'Tab') {
        e.preventDefault();
        this.toggle();
      } else if (e.key === 'Escape') {
        if (this.holding !== null) this.holding = null;
        else if (this.open) this.toggle();
        this.render();
      } else if (/^[1-8]$/.test(e.key)) {
        this.select(Number(e.key) - 1);
      }
    });
  }

  toggle(): void {
    this.open = !this.open;
    this.holding = null;
    $('pack-panel').classList.toggle('hidden', !this.open);
    this.sfx.play(this.open ? 'open' : 'close');
    if (!this.open && (this.chest || this.machine || this.term || this.bench)) {
      this.chest = null;
      this.machine = null;
      this.term = null;
      this.bench = false;
      this.net.send({ t: 'chestClose' });
    }
    this.render();
  }

  /** The enchanter's bench works straight from the pack: lift an affix off an item, or set a gem. */
  private renderBench(): void {
    const pack = this.pack;
    if (!pack) return;
    const all: [number, NonNullable<Slot>][] = [];
    pack.slots.forEach((s, i) => s && all.push([i, s]));
    if (pack.armor) all.push([-1, pack.armor]);
    const gems = all.filter(([, s]) => s.id === 'inscribed_gem' && s.m);
    const magic = all.filter(([, s]) => s.m && s.id !== 'inscribed_gem');
    const souls = count(pack.slots, 'soul_gem');
    const icon = (s: NonNullable<Slot>) => `<span class="mini" style="${iconStyle(this.icons, s.id)}"></span>`;
    const name = (s: NonNullable<Slot>) => `<span style="color:${s.m ? RARITY[s.m.r].color : '#e8dcb0'}">${s.m?.name ?? ITEMS[s.id].name}</span>`;
    let html = `<h2>Enchanter's bench</h2>
      <p class="tip">Lift an affix off an item into a soul gem (the item is destroyed), then set the gem into another weapon, tool or armor (up to 4 affixes; 3 or more makes it rare).</p>
      <h3>Extract <span class="tip">(${souls} soul gem${souls === 1 ? '' : 's'})</span></h3>`;
    html += magic.length
      ? magic.map(([i, s]) => `<div class="bitem">${icon(s)}${name(s)}<div class="baff">${s.m!.a.map(([k, v], j) => `<button class="bx" data-i="${i}" data-k="${j}" ${souls ? '' : 'disabled'}>${AFFIXES[k].text(v)}</button>`).join('')}</div></div>`).join('')
      : '<p class="tip">No magic items in your pack.</p>';
    html += '<h3>Set a gem</h3>';
    const targets = all.filter(([, s]) => s.id !== 'inscribed_gem');
    html += gems.length
      ? gems.map(([gi, g]) => {
          const [k] = g.m!.a[0];
          const ok = targets.filter(([, t]) => !cantSet(t.id, t.m, k));
          return `<div class="bitem">${icon(g)}<b>${AFFIXES[k].text(g.m!.a[0][1])}</b><div class="baff">${ok.length ? ok.map(([ti, t]) => `<button class="ba" data-g="${gi}" data-t="${ti}">→ ${t.m?.name ?? ITEMS[t.id].name}</button>`).join('') : '<span class="tip">Nothing in your pack can take it.</span>'}</div></div>`;
        }).join('')
      : '<p class="tip">No inscribed gems yet.</p>';
    $('bench-col').innerHTML = html;
    for (const b of document.querySelectorAll<HTMLButtonElement>('#bench-col .bx')) b.onclick = () => this.net.send({ t: 'benchExtract', i: Number(b.dataset.i), k: Number(b.dataset.k) });
    for (const b of document.querySelectorAll<HTMLButtonElement>('#bench-col .ba')) b.onclick = () => this.net.send({ t: 'benchApply', gem: Number(b.dataset.g), target: Number(b.dataset.t) });
  }

  showChest(slots: Slot[]): void {
    this.chest = slots;
    this.machine = null;
    this.term = null;
    if (!this.open) this.toggle();
    this.render();
  }

  showBench(): void {
    this.bench = true;
    this.chest = null;
    this.machine = null;
    this.term = null;
    if (!this.open) this.toggle();
    else this.render();
  }

  hideChest(): void {
    if (!this.chest && !this.machine && !this.term && !this.bench) return;
    this.chest = null;
    this.machine = null;
    this.term = null;
    this.bench = false;
    if (this.open) this.toggle();
  }

  showMachine(m: MachineMsg): void {
    this.machine = m;
    this.chest = null;
    this.term = null;
    if (!this.open) this.toggle();
    else this.render();
  }

  showTerm(m: TermMsg): void {
    const fresh = !this.term || this.term.tx !== m.tx || this.term.ty !== m.ty;
    this.term = m;
    this.chest = null;
    this.machine = null;
    if (fresh) ($('term-search') as HTMLInputElement).value = '';
    if (!this.open) this.toggle();
    else this.render();
  }

  select(i: number): void {
    if (!this.pack || i < 0 || i >= HOTBAR || i === this.pack.sel) return;
    this.pack.sel = i;
    this.net.send({ t: 'sel', i });
    this.sfx.play('click', undefined, 0.6);
    this.render();
  }

  /** Re-render the recipe list if walking toward/away from a cooking fire changed what you can make. */
  refreshStation(): void {
    const now = this.nearStation('cooking_fire') || this.nearStation('furnace') ? (this.nearStation('cooking_fire') ? 1 : 0) + (this.nearStation('furnace') ? 2 : 0) : 0;
    if (now !== this.stationWas) {
      this.stationWas = now;
      this.render();
    }
  }

  /** Mouse wheel: step through the hotbar, wrapping around. */
  step(dir: number): void {
    if (!this.pack) return;
    this.select((this.pack.sel + dir + HOTBAR) % HOTBAR);
  }

  update(pack: Pack): void {
    this.pack = pack;
    this.render();
  }

  private clickSlot(i: number): void {
    if (!this.pack) return;
    if (!this.open && i >= 0 && i < HOTBAR) {
      this.select(i);
      return;
    }
    if (this.chest && i >= 0) {
      if (this.pack.slots[i]) this.net.send({ t: 'chestMove', from: 'pack', i });
      return;
    }
    if (this.machine && i >= 0) {
      if (this.pack.slots[i]) this.net.send({ t: 'machineMove', from: 'pack', i });
      return;
    }
    if (this.term && i >= 0) {
      const s = this.pack.slots[i];
      if (s) this.net.send(this.term.drives && DRIVES[s.id] ? { t: 'driveMove', from: 'pack', i } : { t: 'termStore', i });
      return;
    }
    const slot = i === -1 ? this.pack.armor : this.pack.slots[i];
    if (this.holding === null) {
      if (slot) this.holding = i;
    } else {
      if (this.holding !== i) this.net.send({ t: 'swap', a: this.holding, b: i });
      this.holding = null;
      this.sfx.play('click', undefined, 0.6);
    }
    this.render();
  }

  private slotHtml(i: number, slot: Slot, extra = ''): string {
    const sel = this.pack && i === this.pack.sel ? ' sel' : '';
    const held = this.holding === i ? ' holding' : '';
    if (!slot) return `<div class="slot${sel}${held}${extra}" data-i="${i}">${i >= 0 && i < HOTBAR ? `<span class="key">${i + 1}</span>` : ''}</div>`;
    return `<div class="slot${sel}${held}${extra}${rarityClass(slot)}" data-i="${i}" title="${describeSlot(slot)}"><div class="icon" style="${iconStyle(this.icons, slot.id)}"></div>${slot.n > 1 ? `<span class="n">${slot.n}</span>` : ''}${i >= 0 && i < HOTBAR ? `<span class="key">${i + 1}</span>` : ''}</div>`;
  }

  private renderTerm(t: TermMsg): void {
    const icons = this.icons, net = this.net;
    const cell = (id: ItemId | null, n: number, data: string) =>
      id ? `<div class="slot" ${data} title="${describe(ITEMS[id])}${n > 1 ? ` (${n})` : ''}"><div class="icon" style="${iconStyle(icons, id)}"></div>${n > 1 ? `<span class="n">${short(n)}</span>` : ''}</div>` : `<div class="slot" ${data}></div>`;
    $('term-title').textContent = t.drives ? 'Storage core' : 'Storage terminal';
    if (t.drives) {
      const pct = t.coreCap ? Math.round((t.coreUsed! / t.coreCap) * 100) : 0;
      $('term-drives').innerHTML = t.drives.map((s, k) => cell(s?.id ?? null, 1, `data-d="${k}"`)).join('') +
        `<div class="bar">Drives: ${t.coreUsed} / ${t.coreCap} items<div class="mbar"><div style="width:${pct}%"></div></div></div>`;
      for (const el of document.querySelectorAll<HTMLElement>('#term-drives .slot')) el.onclick = () => t.drives?.[Number(el.dataset.d)] && net.send({ t: 'driveMove', from: 'core', i: Number(el.dataset.d) });
    } else $('term-drives').innerHTML = '';
    const q = ($('term-search') as HTMLInputElement).value.trim().toLowerCase();
    const shown = q ? t.items.filter(([id]) => ITEMS[id].name.toLowerCase().includes(q)) : t.items;
    $('term-grid').innerHTML = shown.length ? shown.map(([id, n]) => cell(id, n, `data-t="${id}"`)).join('') : `<div class="tip" style="grid-column:1/-1">${t.items.length ? 'Nothing matches.' : 'Nothing stored yet.'}</div>`;
    for (const el of document.querySelectorAll<HTMLElement>('#term-grid .slot')) el.onclick = (e) => net.send({ t: 'termTake', id: el.dataset.t!, all: e.shiftKey });
    const where = [t.chests ? `${t.chests} chest${t.chests > 1 ? 's' : ''}` : '', t.cores ? `${t.cores} storage core${t.cores > 1 ? 's' : ''}${t.cap ? ` (${t.used} / ${t.cap})` : ' with no drives'}` : ''].filter(Boolean).join(' and ');
    $('term-status').innerHTML = where ? `Connected to ${where}.` : '<span class="miss">Nothing connected. Join chests or a storage core with item pipes.</span>';
  }

  render(): void {
    const p = this.pack;
    if (!p) return;
    $('hotbar').innerHTML = p.slots.slice(0, HOTBAR).map((s, i) => this.slotHtml(i, s)).join('');
    const held = p.slots[p.sel];
    $('held-name').textContent = held ? (held.m ? `${held.m.name} — ${held.m.a.map(([k, v]) => AFFIXES[k].text(v)).join(', ')}` : describe(ITEMS[held.id])) : 'Bare hands — chop trees and pry stones, slowly';
    $('held-name').style.color = held?.m ? RARITY[held.m.r].color : '';
    if (this.open) {
      const rows: string[] = [];
      for (let i = HOTBAR; i < PACK_SIZE; i++) rows.push(this.slotHtml(i, p.slots[i]));
      $('pack-grid').innerHTML = rows.join('');
      $('pack-armor').innerHTML = this.slotHtml(-1, p.armor, ' armor') + '<div class="armor-label">Armor</div>';
      const all = [...p.slots, p.armor, ...(this.term?.items.map(([id, n]) => ({ id, n })) ?? [])];
      const fire = this.nearStation('cooking_fire');
      const forge = this.nearStation('furnace');
      this.stationWas = (fire ? 1 : 0) + (forge ? 2 : 0);
      const at = (st?: string) => (st === 'furnace' ? forge : st === 'cooking_fire' ? fire : true);
      // Search matches the thing you make or anything that goes into it.
      const q = ($('craft-search') as HTMLInputElement).value.trim().toLowerCase();
      const shown = q ? RECIPES.filter((r) => [r.id, ...r.needs.map(([id]) => id)].some((id) => ITEMS[id].name.toLowerCase().includes(q))) : RECIPES;
      $('craft-list').innerHTML = shown.length ? shown.map((r) => {
        const ok = r.needs.every(([id, n]) => count(all, id) >= n) && at(r.station);
        const needs = r.needs
          .map(([id, n]) => `<span class="need ${count(all, id) >= n ? 'have' : 'miss'}" title="${ITEMS[id].name}"><span class="mini" style="${iconStyle(this.icons, id)}"></span>${count(all, id)}/${n}</span>`)
          .join('');
        return `<div class="recipe${ok ? '' : ' cant'}" data-r="${r.id}" title="${describe(ITEMS[r.id])}"><div class="icon" style="${iconStyle(this.icons, r.id)}"></div><div class="rname">${ITEMS[r.id].name}${r.n > 1 ? ` ×${r.n}` : ''}<div class="needs">${needs}${r.station ? `<span class="need ${at(r.station) ? 'have' : 'miss'}">at a ${r.station === 'furnace' ? 'furnace' : 'cooking fire'}</span>` : ''}</div></div></div>`;
      }).join('') : '<div class="tip">Nothing matches.</div>';
    }
    $('machine-col').classList.toggle('hidden', !this.machine);
    if (this.machine) {
      const m = this.machine;
      const def = MACHINES[m.kind as MachineKind];
      const slot = (s: Slot, i: number) =>
        `<div class="mslot"><div class="slot" data-m="${i}" ${s ? `title="${describe(ITEMS[s.id])}"` : ''}>${s ? `<div class="icon" style="${iconStyle(this.icons, s.id)}"></div>${s.n > 1 ? `<span class="n">${s.n}</span>` : ''}` : ''}</div><div class="mlabel">${def.slots[i]}</div></div>`;
      const ins = def.inputs.map((i) => slot(m.slots[i], i)).join('');
      const fuel = def.fuel !== undefined ? slot(m.slots[def.fuel], def.fuel) : '';
      const out = def.output >= 0 ? slot(m.slots[def.output], def.output) : '';
      const status =
        def.power > 0
          ? m.gens ? `Power ${m.power} from ${m.gens} generator${m.gens > 1 ? 's' : ''}` : '<span class="miss">No generator on its power network. Join one with energy cables.</span>'
          : m.burn > 0 ? `Burning: ${m.burn}s of fuel left` : 'Not burning';
      const genInfo =
        m.kind === 'generator' || m.kind === 'alternator' ? `Stored power on this network: ${m.power}`
        : m.kind === 'tap' ? (m.progress > 0 ? 'Dripping resin.' : '<span class="miss">No trees close enough, or it\'s full.</span>')
        : status;
      $('machine-col').innerHTML = `<h2>${def.name}</h2><p class="tip">${def.blurb}</p>
        <div class="mrow">${ins}${fuel}${ins || fuel ? '<div class="arrow">→</div>' : ''}${out}</div>
        ${def.output >= 0 ? `<div class="mbar"><div style="width:${Math.round(m.progress * 100)}%"></div></div>` : ''}
        <p class="mstatus">${genInfo}</p>
        <p class="tip">${m.chests ? `Connected to ${m.chests} chest${m.chests > 1 ? 's' : ''}: it pulls from them and fills them.` : 'Touch a chest or join one with item pipes to automate it.'}</p>
        <p class="tip">Click a stack in your pack to put it in; click a slot to take it out.</p>`;
      for (const el of document.querySelectorAll<HTMLElement>('#machine-col .slot')) el.onclick = () => this.machine?.slots[Number(el.dataset.m)] && this.net.send({ t: 'machineMove', from: 'machine', i: Number(el.dataset.m) });
    }
    $('bench-col').classList.toggle('hidden', !this.bench);
    if (this.bench) this.renderBench();
    $('term-col').classList.toggle('hidden', !this.term);
    if (this.term) this.renderTerm(this.term);
    $('chest-col').classList.toggle('hidden', !this.chest);
    if (this.chest) {
      $('chest-grid').innerHTML = this.chest
        .map((s, i) => (s ? `<div class="slot${rarityClass(s)}" data-c="${i}" title="${describeSlot(s)}"><div class="icon" style="${iconStyle(this.icons, s.id)}"></div>${s.n > 1 ? `<span class="n">${s.n}</span>` : ''}</div>` : `<div class="slot" data-c="${i}"></div>`))
        .join('');
      for (const el of document.querySelectorAll<HTMLElement>('#chest-grid .slot')) el.onclick = () => this.chest?.[Number(el.dataset.c)] && this.net.send({ t: 'chestMove', from: 'chest', i: Number(el.dataset.c) });
    }
    for (const el of document.querySelectorAll<HTMLElement>('#hotbar .slot, #pack-panel .col:not(#chest-col):not(#machine-col):not(#term-col):not(#bench-col) .slot')) el.onclick = () => this.clickSlot(Number(el.dataset.i));
    for (const el of document.querySelectorAll<HTMLElement>('#craft-list .recipe')) {
      el.onclick = () => {
        if (el.classList.contains('cant')) {
          this.sfx.play('deny');
          return;
        }
        this.net.send({ t: 'craft', id: el.dataset.r! });
      };
    }
  }
}

/** 1234 -> 1.2k for the little stack counter. */
function short(n: number): string {
  return n >= 10000 ? `${Math.floor(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export function setHp(hp: number, max: number, wounded: number, full: number): void {
  const fill = $('hp-fill');
  fill.style.width = `${Math.max(0, Math.min(100, (hp / full) * 100))}%`;
  $('hp-cap').style.left = `${Math.min(100, (max / full) * 100)}%`;
  const left = wounded > 0 ? ` · Wounded ${Math.floor(wounded / 60)}:${String(wounded % 60).padStart(2, '0')}` : '';
  $('hp-text').textContent = `${hp} / ${max}${left}`;
  $('hp').classList.toggle('wounded', wounded > 0);
}

export function setEssence(lp: number, cap: number): void {
  const box = $('essence');
  if (!cap && !lp) {
    box.classList.add('hidden');
    return;
  }
  box.classList.remove('hidden');
  $('essence-fill').style.width = `${cap ? Math.min(100, (lp / cap) * 100) : 0}%`;
  $('essence-text').textContent = cap ? `Life essence ${lp} / ${cap}` : `Life essence ${lp} (no orb bound)`;
}
